import React, { useState } from 'react';
import { Box, Typography, Tabs, Tab } from '@mui/material';
import { styled } from '@mui/material/styles';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import HubIcon from '@mui/icons-material/Hub';
import ScienceIcon from '@mui/icons-material/Science';
import { TrainingDocsTab } from './TrainingDocsTab';
import { ConnectorsTab } from './ConnectorsTab';
import { PlaygroundTab } from './PlaygroundTab';
import { Breadcrumbs } from '../../../Breadcrumbs';

const StyledTabs = styled(Tabs)(() => ({
  '& .MuiTabs-indicator': {
    backgroundColor: '#1a202c !important',
    height: 2,
    borderRadius: '2px 2px 0 0',
  },
}));

const StyledTab = styled(Tab)(() => ({
  textTransform: 'none',
  fontWeight: 600,
  minHeight: 48,
  fontSize: '0.875rem',
  color: '#6b7280',
  '&:hover': { color: '#1a202c', backgroundColor: 'transparent' },
  '&.Mui-selected': { color: '#1a202c' },
  '& .MuiTouchRipple-root': { display: 'none' },
}));

const TABS = [
  { label: 'Training Docs', icon: <MenuBookIcon fontSize="small" /> },
  { label: 'Connectors', icon: <HubIcon fontSize="small" /> },
  { label: 'Playground', icon: <ScienceIcon fontSize="small" /> },
];

export const DevHubPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [trainingDocPageId, setTrainingDocPageId] = useState<string | undefined>(undefined);

  const handleGoToTrainingDocs = (pageId?: string) => {
    setTrainingDocPageId(pageId);
    setActiveTab(0);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', bgcolor: 'white' }}>
      {/* Breadcrumbs */}
      <Box sx={{ px: 3, pt: 2 }}>
        <Breadcrumbs disableMargin />
      </Box>

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 3,
          pt: 1.5,
          pb: 0,
          background: 'linear-gradient(145deg, #ffffff 0%, #fafbfc 100%)',
          flexShrink: 0,
          gap: 1.5,
        }}
      >
        <Box sx={{ bgcolor: '#1a202c', borderRadius: 1.5, p: 0.8, display: 'flex', boxShadow: '0 4px 12px rgba(26,32,44,0.25)' }}>
          <ScienceIcon sx={{ fontSize: 20, color: 'white' }} />
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={700} lineHeight={1.2} color="#1a202c">
            Developer Hub
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Docs, connectors &amp; playgrounds
          </Typography>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: '1px solid rgba(0,0,0,0.08)', flexShrink: 0, bgcolor: 'white' }}>
        <StyledTabs
          value={activeTab}
          onChange={(_, v) => { setActiveTab(v); if (v !== 0) setTrainingDocPageId(undefined); }}
          textColor="inherit"
          sx={{ px: 2 }}
        >
          {TABS.map((tab, i) => (
            <StyledTab key={i} label={tab.label} icon={tab.icon} iconPosition="start" />
          ))}
        </StyledTabs>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {activeTab === 0 && <TrainingDocsTab initialPageId={trainingDocPageId} />}
        {activeTab === 1 && <ConnectorsTab />}
        {activeTab === 2 && <PlaygroundTab onGoToTrainingDocs={handleGoToTrainingDocs} />}
      </Box>
    </Box>
  );
};
